import * as preact from "preact";

import * as deviceDetect from "deviceDetect";
import * as keyboardShortcuts from "keyboardShortcuts";
import UserWordModel from "models/UserWordModel";
import models from "models/models";
import * as practiceUtils from "practiceUtils";
import readlangClient from "readlangClient";
import * as languageMap from "shared/languageMap";
import { cn } from "shared/utils";
import AccentInputView from "views/AccentInputView";
import PracticeContextView from "views/PracticeContextView";

interface State {
  inputValue: string;
  clozeSentence: practiceUtils.ClozeSentence;
  submitted: boolean;
  synonymCheck?:
    | {
        type: "checking";
      }
    | {
        type: "result";
        possibleSynonym: boolean;
      };
  hint?: string;
}

class TypingChallengeView extends preact.Component<
  practiceUtils.ChallengeProps,
  State
> {
  userWords: Backbone.Collection<UserWordModel>;
  inputElement: HTMLInputElement;

  constructor(props: practiceUtils.ChallengeProps) {
    super(props);

    this.state = {
      inputValue: "",
      clozeSentence: practiceUtils.getClozeSentence(
        this.props.userWord,
        this.props.selectedContextIndex
      ),
      submitted: false,
    };

    // Register keyboard shortcut for showing hint
    keyboardShortcuts.registerHandler(
      "practicePage",
      keyboardShortcuts.keyCodes.forwardSlash,
      () => {
        this.setState({
          hint: this.getHint(),
        });
        this.unregisterHintShortcut();
      },
      true,
      true,
      deviceDetect.info().mac ? "meta" : "ctrl"
    );
  }

  render() {
    const clozeSentence = this.state.clozeSentence;

    return (
      <div>
        <h2>Type the missing word</h2>
        <p>
          <PracticeContextView
            clozeSentence={clozeSentence}
            mode={
              this.state.submitted
                ? {
                    type: "complete",
                    ttsLanguage: this.props.userWord.get("language"),
                    transliterationLanguage:
                      this.props.userWord.get("language"),
                  }
                : { type: "blanked-out" }
            }
          />
        </p>
        <p>
          Meaning: <strong>{this.props.userWord.get("translation")}</strong>
          {this.props.userWord.get("alternativeTranslations")?.length
            ? `, ${this.props.userWord.get("alternativeTranslations").join(", ")}`
            : ""}
        </p>
        <div class="inputRow">
          <input
            ref={(input) => {
              if (input) {
                this.inputElement = input;
              }
            }}
            key={this.props.userWord.id}
            type="text"
            placeholder={`Type in ${
              languageMap.map[this.props.userWord.get("language")].english
            }`}
            value={
              this.props.skipped
                ? this.props.userWord.get("word")
                : this.state.inputValue
            }
            onInput={(event) => {
              this.setState({ inputValue: event.currentTarget.value });
              this.props.enableCheck(
                event.currentTarget.value.trim().length > 0
              );
            }}
            onKeyDown={(event) => {
              if (event.isComposing || event.keyCode === 229) {
                // Ignore if it's a composition event. This can happen when typing in languages like
                // Korean.
                return;
              }

              if (event.key === "Enter") {
                this.checkAnswer();
                event.stopPropagation();
                event.preventDefault();
              }
            }}
            disabled={this.state.submitted || this.props.skipped}
            autoCapitalize="off"
            autoFocus={!deviceDetect.isSmall()}
            lang={this.props.userWord.get("language")}
          ></input>
        </div>
        <AccentInputView
          getInputElement={() => this.inputElement}
          language={this.props.userWord.get("language")}
          disabled={this.props.skipped}
        />
        <div
          class={cn({
            hint: true,
            submitted: this.state.submitted,
          })}
        >
          {this.state.hint ? (
            <>
              <p>HINT: {this.state.hint}</p>
              <p>
                <button
                  onClick={async () => {
                    await this.setState({ inputValue: "" });
                    // Maybe shouldn't say "Incorrect" in this case - should be neutral
                    this.checkAnswer();
                  }}
                >
                  Show Answer
                </button>
              </p>
            </>
          ) : (
            <button
              disabled={this.props.skipped}
              onClick={() => {
                this.setState({
                  hint: this.getHint(),
                });
                this.unregisterHintShortcut();
                this.inputElement.focus();
              }}
            >
              Show Hint
            </button>
          )}
        </div>
        {this.renderSynonymCheck()}
      </div>
    );
  }

  componentDidMount(): void {
    if (!deviceDetect.isSmall()) {
      this.inputElement.focus();
    }
  }

  unregisterHintShortcut() {
    keyboardShortcuts.unregisterHandler(
      "practicePage",
      keyboardShortcuts.keyCodes.forwardSlash,
      true,
      deviceDetect.info().mac ? "meta" : "ctrl"
    );
  }

  componentWillUnmount(): void {
    this.unregisterHintShortcut();
  }

  getHint() {
    const word = this.props.userWord.get("word");

    if (!word) {
      throw Error("No word to get hint for");
    }

    // Show "starts with" hint 2/3 of the time
    if (Math.random() < 0.67) {
      return `Starts with "${word.substring(0, Math.floor(word.length / 2.5))}..."`;
    } else {
      return `Ends with "...${word.substring(Math.ceil(word.length / 2))}"`;
    }
  }

  renderSynonymCheck() {
    if (!this.state.synonymCheck) {
      return null;
    }

    switch (this.state.synonymCheck?.type) {
      case "checking":
        return (
          <p>
            The original answer was: "{this.props.userWord.get("word")}".
            Checking if your answer is a valid synonym...
          </p>
        );

      case "result":
        return this.state.synonymCheck.possibleSynonym ? (
          <>
            <p>
              Your answer might be a valid synonym of "
              {this.props.userWord.get("word")}". Accept "
              {this.state.inputValue}" as a synonym?
            </p>
            <p>
              <button
                onClick={() => {
                  this.setState({ synonymCheck: undefined });

                  // Add to accepted synonyms for this user word (alternativeWords)
                  const alternativeWords =
                    this.props.userWord.get("alternativeWords") ?? [];
                  alternativeWords.push(this.state.inputValue);
                  this.props.userWord.save(
                    {
                      alternativeWords,
                    },
                    { patch: true }
                  );

                  this.props.completedCallback({
                    type: "show-feedback",
                    correct: "almost",
                  });
                  this.setState({ submitted: true });
                }}
              >
                Yes
              </button>
              <button
                onClick={() => {
                  this.setState({ synonymCheck: undefined });
                  this.reportIncorrect();
                }}
              >
                No
              </button>
            </p>
          </>
        ) : (
          <p>Your answer is not a valid synonym.</p>
        );
    }
  }

  getResult():
    | { type: "correct"; original: boolean }
    | { type: "watch-accent"; original: boolean; closestAnswer?: string }
    | { type: "typo"; original: boolean; closestAnswer?: string }
    | { type: "incorrect" } {
    const originalWordGrade = practiceUtils.gradeTyping(
      this.props.userWord.get("word"),
      this.state.inputValue
    );

    const alternativeWordGrades = this.props.userWord
      .get("alternativeWords")
      .map((word) => practiceUtils.gradeTyping(word, this.state.inputValue));

    // Check for perfect match
    if (originalWordGrade === "correct") {
      return { type: "correct", original: true };
    }
    if (alternativeWordGrades.some((grade) => grade === "correct")) {
      return { type: "correct", original: false };
    }

    // Check for accent mismatch
    if (originalWordGrade === "watch-accent") {
      return { type: "watch-accent", original: true };
    }
    const alternativeWordMatchIndex = alternativeWordGrades.findIndex(
      (alternativeWordGrade) => alternativeWordGrade === "watch-accent"
    );

    if (alternativeWordMatchIndex !== -1) {
      return {
        type: "watch-accent",
        original: false,
        closestAnswer:
          this.props.userWord.get("alternativeWords")[
            alternativeWordMatchIndex
          ],
      };
    }

    // Check for typo
    if (originalWordGrade === "typo") {
      return { type: "typo", original: true };
    }

    const alternativeWordTypoIndex = alternativeWordGrades.findIndex(
      (alternativeWordGrade) => alternativeWordGrade === "typo"
    );

    if (alternativeWordTypoIndex !== -1) {
      return {
        type: "typo",
        original: false,
        closestAnswer:
          this.props.userWord.get("alternativeWords")[alternativeWordTypoIndex],
      };
    }

    // If we've reached here, then we haven't been able to find a match
    return { type: "incorrect" };
  }

  async checkAnswer() {
    const userWord = this.props.userWord;
    const result = this.getResult();

    switch (result.type) {
      case "correct":
        this.props.completedCallback({
          type: "show-feedback",
          correct: "yes",
          message: result.original
            ? undefined
            : {
                title: "Correct alternative!",
                body: `Original word: "${userWord.get("word")}"`,
              },
        });
        this.props.reportRecallEase(
          this.state.hint === undefined ? (result.original ? 5 : 4) : 3
        );
        this.setState({ submitted: true });

        if (models.user.get("speakingMode")) {
          practiceUtils.playTTS(userWord.get("language"), userWord.get("word"));
        }
        break;

      case "watch-accent":
        this.props.completedCallback({
          type: "show-feedback",
          correct: practiceUtils.getLeniency() === "strict" ? "almost" : "yes",
          message: result.original
            ? {
                title: `Watch your accents!`,
                body: `Correct answer: "${userWord.get("word")}"`,
              }
            : {
                title: `Watch your accents!`,
                body: `Correct alternative: "${result.closestAnswer}". Original: "${userWord.get("word")}"`,
              },
          showLeniencySettings: true,
        });
        this.props.reportRecallEase(
          practiceUtils.getLeniency() === "strict" ? 3 : result.original ? 5 : 4
        );
        this.setState({ submitted: true });
        if (models.user.get("speakingMode")) {
          practiceUtils.playTTS(
            userWord.get("language"),
            result.closestAnswer ?? userWord.get("word") // TODO: match capitalization?
          );
        }
        break;

      case "typo":
        this.props.completedCallback({
          type: "show-feedback",
          correct: practiceUtils.getLeniency() !== "strict" ? "almost" : "no",
          message: result.original
            ? {
                title: `Watch the typo!`,
                body: `Correct answer: "${userWord.get("word")}"`,
              }
            : {
                title: `Watch the typo!`,
                body: `Correct alternative: "${result.closestAnswer}". Original: "${userWord.get("word")}"`,
              },
          showLeniencySettings: true,
        });
        this.props.reportRecallEase(
          practiceUtils.getLeniency() === "allowTypos" ? 3 : 4
        );
        this.setState({ submitted: true });
        if (models.user.get("speakingMode")) {
          practiceUtils.playTTS(
            userWord.get("language"),
            result.closestAnswer ?? userWord.get("word") // TODO: match capitalization?
          );
        }
        break;

      case "incorrect":
        // XXX this isn't working reliably enough. too many false positives
        // this.checkForSynonym();

        this.reportIncorrect();
        if (models.user.get("speakingMode")) {
          practiceUtils.playTTS(userWord.get("language"), userWord.get("word"));
        }
        break;
    }
  }

  async checkForSynonym() {
    const userWord = this.props.userWord;

    await this.setState({ synonymCheck: { type: "checking" } });

    readlangClient.request(
      "GET",
      "/api/smartDictionary/askAI",
      {
        prompt: `Is "${this.state.inputValue}" a valid synonym of "${userWord.get("word")}", and mean "${userWord.get("translation")}", and is spelled correctly, and is grammatically correct within the following context:
          
> ${this.state.clozeSentence.map((part) => part.text).join("")}?
          
If it satisfies all four criteria just answer "yes", otherwise just answer "no".`,
        model: "gpt-4o-mini",
      },
      (response) => {
        console.log("Synonym check response", response);
        const potentialSynonym = response.toLowerCase().includes("yes");

        if (potentialSynonym) {
          this.setState({
            synonymCheck: {
              type: "result",
              possibleSynonym: response.toLowerCase().includes("yes"),
            },
          });
        } else {
          this.setState({
            synonymCheck: undefined,
          });
          this.reportIncorrect();
        }
      },
      () => {
        this.reportIncorrect();
      },
      { dataType: "text" }
    );
  }

  reportIncorrect() {
    if (this.state.inputValue.trim().length === 0) {
      // User didn't even try, so don't say Incorrect, and offer the typing mode toggle
      this.props.completedCallback({
        type: "show-feedback",
        correct: "no",
        message: {
          title: "Skipped",
          body: `The correct answer is "${this.props.userWord.get("word")}"`,
        },
        showTypingModeToggle: true,
      });

      this.props.reportRecallEase(2);
      this.setState({ submitted: true });
      return;
    }

    this.props.completedCallback({
      type: "show-feedback",
      correct: "no",
      message: {
        title: "Incorrect",
        body: `The correct answer is "${this.props.userWord.get("word")}"`,
      },
      showLeniencySettings: true,
    });
    this.props.reportRecallEase(2);
    this.setState({ submitted: true });
  }
}

export default TypingChallengeView;
